import React from 'react'
import { Tab } from '@headlessui/react'
import classNames from 'classnames'
import { LabelSM, TitleSM } from '@/atoms/Text'

export type PillTabItem = {
  id: string
  primary: string
  secondary?: string
}

export type PillTabsProps = {
  items: PillTabItem[]
  activeId?: string
  defaultActiveId?: string
  onChange?: (item: PillTabItem) => void
  className?: string
  tabClassName?: string
  ariaLabel?: string
}

export const PillTabs: React.FC<PillTabsProps> = ({
  items,
  activeId,
  defaultActiveId,
  onChange,
  className,
  tabClassName,
  ariaLabel,
}) => {
  const idToIndex = React.useMemo(() => {
    const map = new Map<string, number>()
    items.forEach((it, idx) => {
      if (it?.id) map.set(it.id, idx)
    })
    return map
  }, [items])

  const defaultIndex = React.useMemo(() => {
    if (defaultActiveId && idToIndex.has(defaultActiveId)) return idToIndex.get(defaultActiveId) as number
    return 0
  }, [defaultActiveId, idToIndex])

  const isControlled = typeof activeId !== 'undefined'
  const controlledIndex = isControlled ? idToIndex.get(activeId as string) ?? 0 : undefined
  const [uncontrolledIndex, setUncontrolledIndex] = React.useState<number>(defaultIndex)

  React.useEffect(() => {
    if (!isControlled) {
      if (uncontrolledIndex >= items.length) {
        setUncontrolledIndex(items.length ? 0 : 0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  if (!items || items.length === 0) return null

  const selectedIndex = isControlled ? (controlledIndex as number) : uncontrolledIndex

  const handleChange = (index: number) => {
    const nextItem = items[index]
    if (!isControlled) setUncontrolledIndex(index)
    if (nextItem) onChange?.(nextItem)
  }

  return (
    <Tab.Group selectedIndex={selectedIndex} onChange={handleChange}>
      <Tab.List
        aria-label={ariaLabel}
        className={classNames(
          'gap-x-2 lg:gap-x-0 mb-6 flex flex-row px-4 sm:px-8 md:px-12 lg:mb-0 scrollbar-none lg:flex-col lg:gap-y-[14px] lg:pb-6 xl:px-16 overflow-x-auto touch-pan-x snap-x snap-mandatory lg:snap-none lg:overflow-x-visible',
          className,
        )}
      >
        {items.map((item) => (
          <Tab
            key={item.id}
            className={({ selected }) =>
              classNames(
                'group-pill-tab lg:min-w-[300px] flex flex-row focus-visible:outline-none focus-visible:ring-0 focus-visible:right-offset-0 focus-visible:ring-transparent lg:justify-between py-2 px-4 lg:py-[17px] lg:px-[27px] rounded-full w-fit cursor-pointer transition-colors duration-300 ease-in-out focus:outline-none',
                selected
                  ? 'bg-white text-core-gray-950 lg:text-white lg:bg-[#434343] lg:hover:bg-[#555555]'
                  : 'text-white lg:hover:bg-oxide-primary lg:hover:text-core-gray-950',
                tabClassName,
              )
            }
          >
            <LabelSM weight="semibold" className="lg:photon-title-sm w-fit select-none whitespace-nowrap">
              {item.primary}
            </LabelSM>
            {item.secondary ? (
              <TitleSM
                className={classNames('hidden select-none lg:flex transition-all ease-in-out duration-200', {
                  'group-pill-tab-hover:text-core-gray-950 text-core-gray-500': true,
                  'text-white': false,
                })}
              >
                {item.secondary}
              </TitleSM>
            ) : null}
          </Tab>
        ))}
      </Tab.List>
    </Tab.Group>
  )
}
